/**
 * Conditional Imports Utility
 *
 * Industry best practice: Prevent bundling of feature code when flags are disabled.
 * This ensures hidden features don't consume resources even when disabled.
 *
 * Implementation:
 * - Uses dynamic imports with feature flag checks
 * - Only loads code when feature is actually enabled
 * - Reduces bundle size for production builds
 * - Improves initial load performance
 */

import { lazy, ComponentType, LazyExoticComponent } from "react";

export interface ConditionalImportOptions {
  featureFlag: string;
  enabledCallback?: () => boolean;
}

/**
 * Create a lazy-loaded component that only loads when feature flag is enabled
 *
 * This is more aggressive than standard lazy loading:
 * - Standard lazy: loads on first render attempt
 * - Conditional lazy: only loads if feature flag is enabled
 *
 * @param importFn - Function that imports the component
 * @param options - Configuration including feature flag name
 * @returns Lazy-loaded component or null component
 */
export function createConditionalLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: ConditionalImportOptions
): LazyExoticComponent<T> {
  // CRITICAL: Check feature flag at module load time (build time for production)
  // This allows bundlers to tree-shake unused code
  const isEnabled = checkFeatureFlag(
    options.featureFlag,
    options.enabledCallback
  );

  if (isEnabled) {
    // Feature enabled - lazy load normally
    return lazy(importFn);
  } else {
    // Feature disabled - return a stub component that renders null
    // This prevents the code from being executed but still requires bundling
    // For true code elimination, we need build-time optimization
    return lazy(() =>
      Promise.resolve({
        default: (() => null) as unknown as T,
      })
    );
  }
}

/**
 * Check if a feature flag is enabled
 *
 * Checks in order:
 * 1. Custom callback (for runtime feature flags)
 * 2. Environment variable (VITE_ENABLE_*)
 * 3. Returns false by default
 *
 * IMPORTANT: This is evaluated at module load time (build time),
 * allowing bundlers to eliminate unused code through tree-shaking.
 */
function checkFeatureFlag(
  flagName: string,
  enabledCallback?: () => boolean
): boolean {
  // Check custom callback first (for runtime feature flags from backend)
  if (enabledCallback) {
    try {
      return enabledCallback();
    } catch (error) {
      console.warn(`Feature flag callback error for ${flagName}:`, error);
    }
  }

  // CRITICAL: Check environment variable at build time
  // In production builds, Vite will replace import.meta.env with actual values
  // This allows dead code elimination during the build process
  const envVarName = `VITE_ENABLE_${flagName}`;

  // Access env at module level for better tree-shaking.
  // NOTE: Avoid referencing `import.meta` directly; Jest runs in CJS where it's a syntax error.
  const envValue = (process.env as any)[envVarName];

  if (envValue === "true") {
    return true;
  }

  if (envValue === "false") {
    return false;
  }

  // Default to disabled for safety
  // With this default, dashboards will be excluded from production builds
  // unless explicitly enabled via environment variables
  return false;
}

/**
 * Shorthand for creating conditional lazy components
 *
 * CRITICAL FIX: Now checks runtime feature flags for consistency
 * This ensures ProtectedRoute and conditional imports use the same flag source
 *
 * Usage:
 * const ConditionalProviderDashboard = createConditionalLazy(
 *   () => import('./pages/ProviderDashboard'),
 *   'PROVIDER_DASHBOARD'
 * );
 */
export function createConditionalLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  flagName: string
): LazyExoticComponent<T> {
  return lazy(async () => {
    // CRITICAL FIX: Check runtime feature flags for consistency with ProtectedRoute
    // This ensures component loading aligns with route protection logic
    try {
      // Dynamically import the feature flag service
      const { FeatureFlagService } = await import(
        "../services/featureFlagService"
      );
      const service = FeatureFlagService.getInstance();

      // Wait for initialization
      await service.initialize();

      // Check the flag value
      const flags = service.getAllFlags();
      const isEnabled = flags[flagName as keyof typeof flags] === true;

      if (isEnabled) {
        // Feature enabled - import the component
        return importFn();
      } else {
        // Feature disabled - return null component
        return { default: (() => null) as unknown as T };
      }
    } catch (error) {
      console.error(`Error checking feature flag ${flagName}:`, error);

      // Fallback: Check environment variable at build time
      const envValue = (process.env as any)[`VITE_ENABLE_${flagName}`];
      const isEnvEnabled = envValue === "true";

      if (isEnvEnabled) {
        return importFn();
      } else {
        return { default: (() => null) as unknown as T };
      }
    }
  });
}
